import { Button, Flex, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import * as ct from 'countries-and-timezones';
import SEO from '../components/SEO';
import { useTranslation } from '../i18n/LanguageContext';

export default function PricingPage() {

  const { t } = useTranslation();
  const [timezone, setTimezone] = useState();
  const [usesEuro, setUsesEuro] = useState(false);

  const pricingJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": t("jsonld.pricing.name"),
    "description": t("jsonld.pricing.description"),
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "Bibliona",
      "applicationCategory": "BusinessApplication",
      "offers": [
        {
          "@type": "Offer",
          "name": t("pricing.basic"),
          "price": "0",
          "priceCurrency": "USD",
          "description": t("jsonld.pricing.basic.description"),
        },
        {
          "@type": "Offer",
          "name": t("pricing.pro"),
          "price": "1.99",
          "priceCurrency": "USD",
          "billingIncrement": "month",
          "description": t("jsonld.pricing.pro.description"),
        },
        {
          "@type": "Offer",
          "name": t("pricing.enterprise"),
          "price": "19.99",
          "priceCurrency": "USD",
          "billingIncrement": "month",
          "description": t("jsonld.pricing.enterprise.description"),
        },
      ],
    },
  };

  const locateUser = () => {
    const euroCountries = ['AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'];
    const newTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countries = ct.getCountriesForTimezone(newTimezone);
    let usesEuro = false;
    countries.forEach((country) => {
      if (euroCountries.indexOf(country.id) !== -1) {
        usesEuro = true;
      }
    });
    setUsesEuro(usesEuro)
    setTimezone(newTimezone);
  }

  useEffect(() => {
    if (!timezone) {
      locateUser();
    }
  }, [timezone])

  return (
    <main>
      <SEO
        title={t("seo.pricing.title")}
        description={t("seo.pricing.description")}
        path="/pricing"
        jsonLd={pricingJsonLd}
      />
      <Flex
        flexDirection={"column"}
        alignItems={"center"}
        p={"20px"}
        gap={"50px"}
      >
        <Text
          as="h1"
          fontSize={"40px"}
          color={"text.dark.grey"}
        >{t("pricing.title")}</Text>
        <Flex
          as="section"
          aria-label={t("pricing.aria")}
          gap={3}
          flexWrap={"wrap"}
          flexDirection={["column", "row"]}
        >
          <Flex
            as="article"
            flexDirection={"column"}
            border={"1px solid"}
            borderColor={"divider.coral"}
            borderRadius={"20px"}
            backgroundColor={"background.coral"}
            padding={"20px"}
            flex={"1"}
          >
            <Text as="h2" color="text.medium.coral" fontSize={"inherit"}>{t("pricing.basic")}</Text>
            <Text
              color="text.medium.coral"
              fontSize={"30px"}
              lineHeight={"30px"}
              fontFamily={'var(--font-vidaloka)'}>{t("pricing.free")}</Text>
            <Link
              to={process.env.REACT_APP_BIBLIONA_URL + '/auth/sign-in'}
              target="_blank"
              aria-label={t("pricing.basicAria")}
              >
              <Button
                my={"30px"}
                variant="coralFill"
                width={"100%"}
              >{t("nav.signIn")}</Button>
            </Link>
            <Flex flexDirection={"column"}>
              <Text flexDirection={"column"} color={"text.medium.coral"}>{t("pricing.includes")} </Text>
              <UnorderedList color={"text.medium.coral"}>
                <ListItem>{t("pricing.unlimitedProducts")}</ListItem>
                <ListItem>{t("pricing.unlimitedPersonas")}</ListItem>
                <ListItem>{t("pricing.unlimitedExperiences")}</ListItem>
              </UnorderedList>
            </Flex>
          </Flex>
          <Flex
            as="article"
            flexDirection={"column"}
            border={"1px solid"}
            borderColor={"divider.blue"}
            borderRadius={"20px"}
            backgroundColor={"background.blue"}
            padding={"20px"}
            flex={"1"}
          >
            <Text as="h2" color="text.medium.blue" fontSize={"inherit"}>{t("pricing.pro")}</Text>
            <Flex alignItems={"baseline"}>
              <Text
                color="text.medium.blue"
                fontSize={"30px"}
                fontFamily={'var(--font-vidaloka)'}
                lineHeight={"30px"}
              >{usesEuro ? '1,99 €' : '$1.99'}</Text>
              <Text
                color="text.medium.blue"
                fontSize={"20px"}
                fontFamily={'var(--font-vidaloka)'}
                lineHeight={"30px"}
              >{t("pricing.perMonth")}</Text>
            </Flex>
            <Link 
              to={process.env.REACT_APP_BIBLIONA_URL + '/auth/sign-in?redirect_url=/profile&arg=modal=upgrade-plan'}
              target="_blank"
              aria-label={t("pricing.proAria")}
            >
              <Button
                my={"30px"}
                variant="blueFill"
                width={"100%"}
              >{t("pricing.select")}</Button>
            </Link>
            <Flex flexDirection={"column"}>
              <Text flexDirection={"column"} color={"text.medium.blue"} >{t("pricing.includes")} </Text>
              <UnorderedList color={"text.medium.blue"}>
                <ListItem>{t("pricing.sharingFeatures")}</ListItem>
                <ListItem>{t("pricing.unlimitedProducts")}</ListItem>
                <ListItem>{t("pricing.unlimitedPersonas")}</ListItem>
                <ListItem>{t("pricing.unlimitedExperiences")}</ListItem>
              </UnorderedList>
            </Flex>
          </Flex>
          <Flex
            as="article"
            flexDirection={"column"}
            border={"1px solid"}
            borderColor={"divider.purple"}
            borderRadius={"20px"}
            backgroundColor={"background.purple"}
            padding={"20px"}
            flex={1}
          >
            <Text as="h2" color="text.medium.purple" fontSize={"inherit"}>{t("pricing.enterprise")}</Text>
            <Flex alignItems={"baseline"}>
                <Text
                  color="text.medium.purple"
                  fontSize={"30px"}
                  fontFamily={'var(--font-vidaloka)'}
                  lineHeight={"30px"}
                >{usesEuro ? '19,99 €' : '$19.99'}</Text>
                <Text
                  color="text.medium.purple"
                  fontSize={"20px"}
                  fontFamily={'var(--font-vidaloka)'}
                  lineHeight={"30px"}
                >{t("pricing.perUserMonth")}</Text>
            </Flex>
            <Link
              to={process.env.REACT_APP_BIBLIONA_URL + '/auth/sign-in?redirect_url=/profile&arg=modal=upgrade-plan'}
              target="_blank"
              aria-label={t("pricing.enterpriseAria")}
            >
                <Button
                  my={"30px"}
                  variant="purpleFill"
                  width={"100%"}
                >{t("pricing.select")}</Button>
            </Link>
            <Flex flexDirection={"column"}>
              <Text flexDirection={"column"} color={"text.medium.purple"} >{t("pricing.includes")} </Text>
              <UnorderedList color={"text.medium.purple"}>
                <ListItem>{t("pricing.libraryAccess")}</ListItem>
                <ListItem>{t("pricing.sharingFeatures")}</ListItem>
                <ListItem>{t("pricing.commonBilling")}</ListItem>
                <ListItem>{t("pricing.unlimitedProducts")}</ListItem>
                <ListItem>{t("pricing.unlimitedPersonas")}</ListItem>
                <ListItem>{t("pricing.unlimitedExperiences")}</ListItem>
              </UnorderedList>
            </Flex>
          </Flex>
        </Flex>
        <Flex as="nav" aria-label={t("pricing.additionalNav")} flexDirection={"column"} alignItems={"center"}>
          <Link to="/features">
            <Button width={"170px"} variant="greyFill" size={"md"} mb={2}>{t("pricing.seeFeatures")}</Button>
          </Link>
          <Link to="https://support.bibliona.com/" target='_blank'>
            <Button width={"170px"} variant="greyOutline" size={"md"} mb={2}>{t("pricing.contactUs")}</Button>
          </Link>
        </Flex>
      </Flex>
    </main>
  );
}
